/**
 * What the sender has actually done lately, across every campaign.
 *
 * WITH THE CAMPAIGN'S NAME, which is the whole reason this table exists beside
 * the automation page's own. That one lists the last twenty batches with no
 * subject: eight rows of "40 sent, 3 skipped" tell an operator nothing about
 * which letter went where. The name is what turns a run log into a history.
 *
 * A RUN OUTLIVES ITS CAMPAIGN. The relation is `SetNull`, so deleting a
 * campaign leaves its batches in the history with no name. They are drawn as
 * "deleted campaign" rather than dropped - the record of a send is the thing
 * worth keeping when the reason for it has gone.
 */
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { OverviewRun } from "../../../../lib/marketingOverview";
import { Panel, dt, num } from "../shared";

export function RunsCard({ runs }: { runs: OverviewRun[] }) {
  const t = useT();
  return (
    <Panel
      title={t("marketing.overview.runs.title")}
      description={t("marketing.overview.runs.intro")}
      actions={
        <Link to="/marketing/automation" className="crm-btn">
          {t("nav.marketingAutomation")}
          <ChevronRight className="size-4" />
        </Link>
      }
    >
      {runs.length === 0 ? (
        <p className="crm-sub">{t("marketing.run.historyEmpty")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="crm-table w-full">
            <thead>
              <tr>
                <th>{t("marketing.run.colStarted")}</th>
                <th>{t("marketing.overview.runs.colCampaign")}</th>
                <th>{t("marketing.run.colTrigger")}</th>
                <th className="text-right">{t("marketing.run.colSent")}</th>
                <th className="text-right">{t("marketing.run.colSkipped")}</th>
                <th className="text-right">{t("marketing.run.colFailed")}</th>
                <th className="pl-4">{t("marketing.run.colState")}</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td className="whitespace-nowrap">{dt(r.startedAt)}</td>
                  <td className="min-w-0">
                    {r.campaignId && r.campaignName ? (
                      <Link to={`/marketing/campaigns/${r.campaignId}`} className="crm-trunc block">
                        {r.campaignName}
                      </Link>
                    ) : (
                      <span className="crm-mut">{t("marketing.overview.runs.deleted")}</span>
                    )}
                  </td>
                  <td>{t(`marketing.run.trigger.${r.trigger}`)}</td>
                  <td className="crm-tnum text-right">{num(r.sent)}</td>
                  <td className="crm-tnum text-right">{num(r.skipped)}</td>
                  <td className="crm-tnum text-right">{num(r.failed)}</td>
                  <td className="crm-sub pl-4">
                    {/* The error first, because a batch that went wrong is the
                        row somebody opened this table to find. It is stored as
                        free text - the sender's own message, not one of the
                        translated stop codes - so it is printed as written
                        rather than looked up and silently rendered as a key. */}
                    {r.error ?? (r.finishedAt ? t("marketing.run.finished") : t("marketing.run.running"))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
